import { useState, useMemo, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Plus,
  Package,
  Layers,
  ImageIcon,
  Check,
  ChevronsUpDown,
  Pencil,
  Trash2,
  X,
} from '@/lib/icons';

import {
  getDeviceModelById,
  getModelVariants,
  updateDeviceModel,
  createVariant,
  updateVariant,
  deleteVariant,
  deleteDeviceModel,
  getDeviceModelImages,
  uploadDeviceModelImage,
  deleteDeviceImage,
  setDeviceImagePrimary,
  getDeviceVariantImages,
  uploadDeviceVariantImage,
} from '@/features/device-models/api';
import {
  deviceCatalogKeys,
  invalidateDeviceCatalog,
} from '@/features/device-models/keys';
import { hasPermission } from '@/lib/auth/permissions';
import ImageUpload from '@/components/common/image-upload';
import { ButtonSpinner } from '@/components/feedback';
import {
  CreateDeviceVariantSchema,
  type CreateDeviceVariant,
  type DeviceVariantListItem,
} from '@/features/device-models/device-model';
import { useToast } from '@/hooks/use-toast';
import { useAppSelector } from '@/store/hooks';
import { cn, formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import EditableTextField from '@/components/ui/editable-text-field';
import EditableBooleanField from '@/components/ui/editable-boolean-field';
import { RecordLink } from '@/components/ui/record-link';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export const Route = createFileRoute('/_layout/device-models/$modelId')({
  component: DeviceModelDetailPage,
});

const categoryBadge = (category: string) => {
  const map: Record<string, { cls: string; label: string }> = {
    phone: {
      cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
      label: 'Phone',
    },
    tablet: {
      cls: 'bg-violet-500/15 text-violet-600 border-violet-500/30 dark:text-violet-400',
      label: 'Tablet',
    },
    laptop: {
      cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
      label: 'Laptop',
    },
    accessory: {
      cls: 'bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400',
      label: 'Accessory',
    },
  };
  const s = map[category] ?? { cls: '', label: category };
  return <Badge className={s.cls}>{s.label}</Badge>;
};

function DeviceModelDetailPage() {
  const { modelId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';
  const currentUser = useAppSelector((state) => state.auth.user);
  const canUpdateVariant = hasPermission(
    currentUser,
    'device_models',
    'update'
  );
  const canCreateVariant = hasPermission(
    currentUser,
    'device_models',
    'create'
  );
  const canDeleteVariant = hasPermission(
    currentUser,
    'device_models',
    'delete'
  );
  const [addVariantOpen, setAddVariantOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isUploadingModelImage, setIsUploadingModelImage] = useState(false);
  // Track which variant row (if any) is currently editing so we can widen the
  // shared column widths only during edit. Keeping this lifted lets the read
  // view stay compact (no horizontal scroll) until the user actually edits.
  const [editingVariantId, setEditingVariantId] = useState<string | null>(null);
  const isEditingAnyVariant = editingVariantId !== null;

  const { data: modelData, isLoading: modelLoading } = useQuery({
    queryKey: deviceCatalogKeys.model(modelId),
    queryFn: () => getDeviceModelById(modelId),
  });

  const { data: variantsData, isLoading: variantsLoading } = useQuery({
    queryKey: deviceCatalogKeys.modelVariants(modelId),
    queryFn: () => getModelVariants(modelId),
  });

  const { data: imagesData } = useQuery({
    queryKey: deviceCatalogKeys.modelImages(modelId),
    queryFn: () => getDeviceModelImages(modelId),
  });

  const modelImages = imagesData?.value ?? [];

  const handleModelImageUpload = async (file: File) => {
    setIsUploadingModelImage(true);
    try {
      const result = await uploadDeviceModelImage(modelId, file);
      if (result.succeeded) {
        invalidateDeviceCatalog(queryClient);
        toast({
          title: t('common.success'),
          description: t('images.toast.uploaded', {
            defaultValue: 'Image uploaded successfully',
          }),
        });
      }
    } catch {
      toast({
        title: t('common.error'),
        description: t('images.toast.uploadFailed', {
          defaultValue: 'Failed to upload image',
        }),
        variant: 'destructive',
      });
    } finally {
      setIsUploadingModelImage(false);
    }
  };

  const handleModelImageDelete = async (imageId: string) => {
    const result = await deleteDeviceImage(imageId);
    if (result.succeeded) {
      invalidateDeviceCatalog(queryClient);
      toast({
        title: t('common.success'),
        description: t('images.toast.deleted', {
          defaultValue: 'Image deleted',
        }),
      });
    }
  };

  const handleModelImageSetPrimary = async (imageId: string) => {
    const result = await setDeviceImagePrimary(imageId);
    if (result.succeeded) {
      invalidateDeviceCatalog(queryClient);
    }
  };

  // handleApiResponse throws on succeeded=false (including 4xx like
  // "Cannot delete model with active inventory units"), so we handle both
  // paths: onSuccess = actually deleted, onError = backend or network failure.
  const deleteMutation = useMutation({
    mutationFn: () => deleteDeviceModel(modelId),
    onSuccess: () => {
      invalidateDeviceCatalog(queryClient);
      setDeleteOpen(false);
      toast({
        title: t('common.success'),
        description: t('deviceModels.toast.modelDeleted'),
      });
      navigate({ to: '/device-models' });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : t('deviceModels.toast.modelDeleteFailed');
      toast({
        title: t('common.error'),
        description: message,
        variant: 'destructive',
      });
      // Keep the dialog open so the user can read the error and retry
      // or cancel — closing it here would feel like silent failure.
    },
  });

  const handleFieldSave = async (
    field: string,
    value: string | number | boolean
  ) => {
    const result = await updateDeviceModel(modelId, { [field]: value });
    if (!result.succeeded) {
      throw new Error(
        result.errors?.[0] ?? t('deviceModels.toast.modelUpdateFailed')
      );
    }
    invalidateDeviceCatalog(queryClient);
    toast({
      title: t('common.success'),
      description: t('deviceModels.toast.modelUpdated'),
    });
  };

  const model = modelData?.value;
  const variants = variantsData?.value ?? [];

  if (modelLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="lg:col-span-2 h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{t('common.error')}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: '/device-models' })}
        >
          {t('common.actions.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/device-models' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800">
            <Package className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">
              {model.brand} {model.name}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t('deviceModels.detail.subtitle', {
                count: model.variantCount,
              })}
            </p>
          </div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {categoryBadge(model.category)}
          {model.isActive ? (
            <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
              {t('common.status.active')}
            </Badge>
          ) : (
            <Badge className="bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400">
              {t('common.status.inactive')}
            </Badge>
          )}
          {canDeleteVariant && (
            <AlertDialog
              open={deleteOpen}
              onOpenChange={(next) => {
                // Lock the dialog closed while a delete is in-flight so users
                // can't dismiss mid-request and end up thinking it silently
                // failed. The mutation's onSuccess/onError handles closing.
                if (deleteMutation.isPending) return;
                setDeleteOpen(next);
              }}
            >
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  {t('common.actions.delete')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('deviceModels.delete.title')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('deviceModels.delete.description', {
                      name: `${model.brand} ${model.name}`,
                    })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleteMutation.isPending}>
                    {t('common.actions.cancel')}
                  </AlertDialogCancel>
                  {/* Regular Button (not AlertDialogAction) so the dialog
                    stays open while the mutation runs — lets the user see
                    the spinner + disabled state instead of the dialog
                    snapping shut on click. */}
                  <Button
                    variant="destructive"
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    aria-busy={deleteMutation.isPending}
                    className="min-w-[96px]"
                  >
                    {deleteMutation.isPending ? (
                      <>
                        <ButtonSpinner className="mr-2" />
                        {t('common.actions.deleting')}
                      </>
                    ) : (
                      t('common.actions.delete')
                    )}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Main layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: Model details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('deviceModels.form.modelInfo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                <EditableTextField
                  variant="compact"
                  label={t('deviceModels.form.brand')}
                  value={model.brand}
                  onSave={(v) => handleFieldSave('brand', v)}
                  disabled={!canUpdateVariant}
                />
                <EditableTextField
                  variant="compact"
                  label={t('deviceModels.form.name')}
                  value={model.name}
                  onSave={(v) => handleFieldSave('name', v)}
                  disabled={!canUpdateVariant}
                />
                <EditableTextField
                  variant="compact"
                  label={t('deviceModels.form.description')}
                  value={model.description ?? ''}
                  onSave={(v) => handleFieldSave('description', v)}
                  placeholder="-"
                  disabled={!canUpdateVariant}
                />
                <EditableTextField
                  variant="compact"
                  label={t('deviceModels.form.imageUrl')}
                  value={model.imageUrl ?? ''}
                  onSave={(v) => handleFieldSave('imageUrl', v)}
                  placeholder="-"
                  disabled={!canUpdateVariant}
                />
                <EditableBooleanField
                  layout="compact"
                  variant="switch"
                  label={t('deviceModels.columns.status')}
                  value={model.isActive}
                  onChange={(v) => handleFieldSave('isActive', v)}
                  disabled={!canUpdateVariant}
                  trueLabel={t('common.status.active')}
                  falseLabel={t('common.status.inactive')}
                />
              </div>
            </CardContent>
          </Card>

          {/* Variants table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  {t('deviceModels.variants.title')}
                </CardTitle>
                {canCreateVariant && (
                  <Dialog
                    open={addVariantOpen}
                    onOpenChange={setAddVariantOpen}
                  >
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('deviceModels.variants.addVariant')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {t('deviceModels.variants.addVariant')}
                        </DialogTitle>
                      </DialogHeader>
                      <AddVariantForm
                        modelId={modelId}
                        existingVariants={variants}
                        onSuccess={() => {
                          setAddVariantOpen(false);
                          invalidateDeviceCatalog(queryClient);
                        }}
                      />
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {variantsLoading ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : variants.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">
                    {t('deviceModels.variants.empty.title')}
                  </p>
                  <p className="text-sm">
                    {t('deviceModels.variants.empty.description')}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40">
                        <TableHead
                          className={isEditingAnyVariant ? 'min-w-[120px]' : ''}
                        >
                          {t('deviceModels.variants.columns.storage')}
                        </TableHead>
                        <TableHead
                          className={isEditingAnyVariant ? 'min-w-[120px]' : ''}
                        >
                          {t('deviceModels.variants.columns.color')}
                        </TableHead>
                        <TableHead
                          className={isEditingAnyVariant ? 'min-w-[100px]' : ''}
                        >
                          {t('deviceModels.variants.columns.ram')}
                        </TableHead>
                        <TableHead
                          className={isEditingAnyVariant ? 'min-w-[140px]' : ''}
                        >
                          {t('deviceModels.variants.columns.sku')}
                        </TableHead>
                        <TableHead
                          className={isEditingAnyVariant ? 'min-w-[140px]' : ''}
                        >
                          {t('deviceModels.variants.columns.purchasePrice')}
                        </TableHead>
                        <TableHead
                          className={isEditingAnyVariant ? 'min-w-[140px]' : ''}
                        >
                          {t('deviceModels.variants.columns.retailPrice')}
                        </TableHead>
                        <TableHead
                          className={isEditingAnyVariant ? 'min-w-[90px]' : ''}
                        >
                          {t('deviceModels.columns.status')}
                        </TableHead>
                        <TableHead className="w-[110px]">
                          {t('deviceModels.variants.columns.units')}
                        </TableHead>
                        <TableHead className="w-[70px]">
                          {t('images.column', {
                            defaultValue: 'Images',
                          })}
                        </TableHead>
                        {(canUpdateVariant || canDeleteVariant) && (
                          <TableHead className="w-[120px] text-right">
                            {t('deviceModels.variants.columns.actions')}
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {variants.map((v) => (
                        <EditableVariantRow
                          key={v.id}
                          variant={v}
                          currency={currency}
                          canUpdate={canUpdateVariant}
                          canDelete={canDeleteVariant}
                          isEditing={editingVariantId === v.id}
                          isAnyEditing={isEditingAnyVariant}
                          onBeginEdit={() => setEditingVariantId(v.id)}
                          onCancelEdit={() => setEditingVariantId(null)}
                          onChanged={() => {
                            setEditingVariantId(null);
                            invalidateDeviceCatalog(queryClient);
                          }}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('deviceModels.detail.quickInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow
                label={t('deviceModels.columns.category')}
                value={categoryBadge(model.category)}
              />
              <InfoRow
                label={t('deviceModels.columns.status')}
                value={
                  model.isActive ? (
                    <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
                      {t('common.status.active')}
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400">
                      {t('common.status.inactive')}
                    </Badge>
                  )
                }
              />
              <InfoRow
                label={t('deviceModels.columns.variants')}
                value={
                  <span className="font-semibold">{model.variantCount}</span>
                }
              />
              <InfoRow
                label={t('deviceModels.columns.totalStock')}
                value={
                  <span className="font-semibold">{model.totalStock}</span>
                }
              />
            </CardContent>
          </Card>

          {/* Model Images */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                {t('images.modelImages', {
                  defaultValue: 'Product Images',
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ImageUpload
                images={modelImages}
                onUpload={handleModelImageUpload}
                onDelete={handleModelImageDelete}
                onSetPrimary={handleModelImageSetPrimary}
                isUploading={isUploadingModelImage}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
}: Readonly<{ label: string; value: React.ReactNode }>) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

// Flat, table-cell-friendly input style. Overrides the default Input pill
// styling (rounded-xl + heavy bg fill) which clips values inside narrow
// table cells. Uses a thin border so text is fully visible.
const INLINE_INPUT_CLS =
  'h-9 w-full min-w-0 rounded-md border border-input bg-background px-2.5 py-1 text-sm font-medium shadow-sm placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-primary/40';

const currencySymbol = (currency: string): string => {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
};

function CurrencyInput({
  value,
  onChange,
  onKeyDown,
  invalid,
  currency,
  emphasize,
}: Readonly<{
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  invalid: boolean;
  currency: string;
  emphasize?: boolean;
}>) {
  const symbol = currencySymbol(currency);
  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5 text-xs font-medium text-muted-foreground">
        {symbol}
      </span>
      <Input
        type="number"
        step="0.01"
        min="0"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        className={cn(
          INLINE_INPUT_CLS,
          'pl-7 tabular-nums text-right',
          emphasize && 'font-semibold',
          invalid &&
            'border-destructive ring-1 ring-destructive/30 focus-visible:ring-destructive'
        )}
      />
    </div>
  );
}

type EditableVariantRowProps = Readonly<{
  variant: DeviceVariantListItem;
  currency: string;
  canUpdate: boolean;
  canDelete: boolean;
  isEditing: boolean;
  isAnyEditing: boolean;
  onBeginEdit: () => void;
  onCancelEdit: () => void;
  onChanged: () => void;
}>;

type VariantDraft = {
  storageCapacity: string;
  color: string;
  ram: string;
  purchasePrice: string;
  retailPrice: string;
  isActive: boolean;
};

const variantToDraft = (v: DeviceVariantListItem): VariantDraft => ({
  storageCapacity: v.storageCapacity ?? '',
  color: v.color ?? '',
  ram: v.ram ?? '',
  purchasePrice: String(v.purchasePrice),
  retailPrice: String(v.retailPrice),
  isActive: v.isActive,
});

/** The units list scoped to one variant — "where are these, and how many". */
function VariantUnitsLink({ variantId }: Readonly<{ variantId: string }>) {
  const { t } = useTranslation();
  return (
    <RecordLink
      to="/inventory/units"
      search={{ variantId }}
      className="text-sm"
      aria-label={t('deviceModels.variants.viewUnits')}
    >
      {t('deviceModels.variants.viewUnits')}
    </RecordLink>
  );
}

function EditableVariantRow({
  variant,
  currency,
  canUpdate,
  canDelete,
  isEditing,
  isAnyEditing,
  onBeginEdit,
  onCancelEdit,
  onChanged,
}: EditableVariantRowProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [draft, setDraft] = useState<VariantDraft>(() =>
    variantToDraft(variant)
  );

  // Reset draft whenever this row enters edit mode (or the underlying variant
  // changes between edits) so stale buffers can't leak across rows.
  useEffect(() => {
    if (isEditing) setDraft(variantToDraft(variant));
  }, [isEditing, variant]);

  const updateMutation = useMutation({
    mutationFn: () => {
      const purchase = Number(draft.purchasePrice);
      const retail = Number(draft.retailPrice);
      return updateVariant(variant.id, {
        storageCapacity: draft.storageCapacity.trim() || undefined,
        color: draft.color.trim() || undefined,
        ram: draft.ram.trim() || undefined,
        purchasePrice: purchase,
        retailPrice: retail,
        isActive: draft.isActive,
      });
    },
    onSuccess: () => {
      toast({
        title: t('common.success'),
        description: t('deviceModels.toast.variantUpdated'),
      });
      onChanged();
    },
    onError: (error: Error) => {
      const message =
        'errors' in error &&
        Array.isArray((error as any).errors) &&
        (error as any).errors.length > 0
          ? (error as any).errors[0]
          : (error.message ?? t('deviceModels.toast.variantUpdateFailed'));
      toast({
        title: t('common.error'),
        description: message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteVariant(variant.id),
    onSuccess: () => {
      toast({
        title: t('common.success'),
        description: t('deviceModels.toast.variantDeleted'),
      });
      setConfirmDeleteOpen(false);
      onChanged();
    },
    onError: (error: Error) => {
      const message =
        'errors' in error &&
        Array.isArray((error as any).errors) &&
        (error as any).errors.length > 0
          ? (error as any).errors[0]
          : (error.message ?? t('deviceModels.toast.variantDeleteFailed'));
      toast({
        title: t('common.error'),
        description: message,
        variant: 'destructive',
      });
    },
  });

  const showActions = canUpdate || canDelete;
  const purchaseInvalid =
    draft.purchasePrice === '' ||
    Number.isNaN(Number(draft.purchasePrice)) ||
    Number(draft.purchasePrice) < 0;
  const retailInvalid =
    draft.retailPrice === '' ||
    Number.isNaN(Number(draft.retailPrice)) ||
    Number(draft.retailPrice) <= 0;
  const canSave = !purchaseInvalid && !retailInvalid;

  const onEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canSave && !updateMutation.isPending) {
      e.preventDefault();
      updateMutation.mutate();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancelEdit();
    }
  };

  if (isEditing) {
    return (
      <TableRow className="bg-primary/5 hover:bg-primary/5 ring-1 ring-inset ring-primary/20">
        <TableCell className="py-2 align-middle">
          <Input
            autoFocus
            value={draft.storageCapacity}
            onChange={(e) =>
              setDraft((d) => ({ ...d, storageCapacity: e.target.value }))
            }
            onKeyDown={onEditKeyDown}
            className={INLINE_INPUT_CLS}
            placeholder="128GB"
          />
        </TableCell>
        <TableCell className="py-2 align-middle">
          <Input
            value={draft.color}
            onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
            onKeyDown={onEditKeyDown}
            className={INLINE_INPUT_CLS}
            placeholder="Black"
          />
        </TableCell>
        <TableCell className="py-2 align-middle">
          <Input
            value={draft.ram}
            onChange={(e) => setDraft((d) => ({ ...d, ram: e.target.value }))}
            onKeyDown={onEditKeyDown}
            className={INLINE_INPUT_CLS}
            placeholder="8GB"
          />
        </TableCell>
        <TableCell className="py-2 align-middle">
          <span
            className="font-mono text-xs text-muted-foreground"
            title={t('deviceModels.variants.skuAutoHint')}
          >
            {variant.sku ?? '-'}
            <span className="sr-only">
              {' '}
              {t('deviceModels.variants.skuAutoHint')}
            </span>
          </span>
        </TableCell>
        <TableCell className="py-2 align-middle">
          <CurrencyInput
            value={draft.purchasePrice}
            onChange={(v) => setDraft((d) => ({ ...d, purchasePrice: v }))}
            onKeyDown={onEditKeyDown}
            invalid={purchaseInvalid}
            currency={currency}
          />
        </TableCell>
        <TableCell className="py-2 align-middle">
          <CurrencyInput
            value={draft.retailPrice}
            onChange={(v) => setDraft((d) => ({ ...d, retailPrice: v }))}
            onKeyDown={onEditKeyDown}
            invalid={retailInvalid}
            currency={currency}
            emphasize
          />
        </TableCell>
        <TableCell className="py-2 align-middle">
          <Switch
            checked={draft.isActive}
            onCheckedChange={(checked) =>
              setDraft((d) => ({ ...d, isActive: checked }))
            }
          />
        </TableCell>
        <TableCell className="py-2 align-middle">
          <VariantUnitsLink variantId={variant.id} />
        </TableCell>
        <TableCell className="py-2 align-middle">
          <VariantImageDialog variantId={variant.id} />
        </TableCell>
        <TableCell className="py-2 align-middle text-right">
          <div className="flex items-center justify-end gap-1.5">
            <Button
              size="sm"
              className="h-8 px-3"
              onClick={() => updateMutation.mutate()}
              disabled={updateMutation.isPending || !canSave}
              aria-label={t('common.actions.save')}
            >
              {updateMutation.isPending ? (
                <ButtonSpinner />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onCancelEdit}
              disabled={updateMutation.isPending}
              aria-label={t('common.actions.cancel')}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow className="hover:bg-muted/30 group">
      <TableCell>{variant.storageCapacity ?? '-'}</TableCell>
      <TableCell>{variant.color ?? '-'}</TableCell>
      <TableCell>{variant.ram ?? '-'}</TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground">
        {variant.sku ?? '-'}
      </TableCell>
      <TableCell>{formatCurrency(variant.purchasePrice, currency)}</TableCell>
      <TableCell className="font-semibold">
        {formatCurrency(variant.retailPrice, currency)}
      </TableCell>
      <TableCell>
        {variant.isActive ? (
          <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
            {t('common.status.active')}
          </Badge>
        ) : (
          <Badge className="bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400">
            {t('common.status.inactive')}
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <VariantUnitsLink variantId={variant.id} />
      </TableCell>
      <TableCell>
        <VariantImageDialog variantId={variant.id} />
      </TableCell>
      {showActions && (
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {canUpdate && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onBeginEdit}
                disabled={isAnyEditing}
                aria-label={t('deviceModels.variants.edit')}
                title={t('deviceModels.variants.edit')}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {canDelete && (
              <AlertDialog
                open={confirmDeleteOpen}
                onOpenChange={(next) => {
                  if (deleteMutation.isPending) return;
                  setConfirmDeleteOpen(next);
                }}
              >
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    aria-label={t('deviceModels.variants.delete')}
                    title={t('deviceModels.variants.delete')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('deviceModels.variants.deleteConfirm.title')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('deviceModels.variants.deleteConfirm.description')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={deleteMutation.isPending}>
                      {t('common.actions.cancel')}
                    </AlertDialogCancel>
                    <Button
                      variant="destructive"
                      onClick={() => deleteMutation.mutate()}
                      disabled={deleteMutation.isPending}
                      className="min-w-[96px]"
                    >
                      {deleteMutation.isPending ? (
                        <>
                          <ButtonSpinner className="mr-2" />
                          {t('common.actions.deleting')}
                        </>
                      ) : (
                        t('common.actions.delete')
                      )}
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}

function VariantImageDialog({ variantId }: Readonly<{ variantId: string }>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { data: imagesData } = useQuery({
    queryKey: deviceCatalogKeys.variantImages(variantId),
    queryFn: () => getDeviceVariantImages(variantId),
    enabled: open,
  });

  const variantImages = imagesData?.value ?? [];

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const result = await uploadDeviceVariantImage(variantId, file);
      if (result.succeeded) {
        invalidateDeviceCatalog(queryClient);
        toast({
          title: t('common.success'),
          description: t('images.toast.uploaded', {
            defaultValue: 'Image uploaded successfully',
          }),
        });
      }
    } catch {
      toast({
        title: t('common.error'),
        description: t('images.toast.uploadFailed', {
          defaultValue: 'Failed to upload image',
        }),
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (imageId: string) => {
    const result = await deleteDeviceImage(imageId);
    if (result.succeeded) {
      invalidateDeviceCatalog(queryClient);
      toast({
        title: t('common.success'),
        description: t('images.toast.deleted', {
          defaultValue: 'Image deleted',
        }),
      });
    }
  };

  const handleSetPrimary = async (imageId: string) => {
    const result = await setDeviceImagePrimary(imageId);
    if (result.succeeded) {
      invalidateDeviceCatalog(queryClient);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <ImageIcon className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            {t('images.variantImages', {
              defaultValue: 'Variant Images',
            })}
          </DialogTitle>
        </DialogHeader>
        <ImageUpload
          images={variantImages}
          onUpload={handleUpload}
          onDelete={handleDelete}
          onSetPrimary={handleSetPrimary}
          isUploading={isUploading}
        />
      </DialogContent>
    </Dialog>
  );
}

// ── Preset options for common device specs ──────────────────────────
const COMMON_STORAGE_OPTIONS = [
  '32GB',
  '64GB',
  '128GB',
  '256GB',
  '512GB',
  '1TB',
];

const COMMON_RAM_OPTIONS = [
  '2GB',
  '3GB',
  '4GB',
  '6GB',
  '8GB',
  '12GB',
  '16GB',
  '32GB',
];

const COMMON_COLOR_OPTIONS = [
  'Black',
  'White',
  'Silver',
  'Gold',
  'Blue',
  'Red',
  'Green',
  'Purple',
  'Graphite',
  'Midnight',
  'Starlight',
];

// ── Combobox field for variant specs ────────────────────────────────
function VariantComboboxField({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
}: Readonly<{
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
}>) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal h-10',
            !value && 'text-muted-foreground/70'
          )}
          type="button"
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 z-[100] border-0 shadow-lg rounded-xl overflow-hidden"
        align="start"
        side="bottom"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            onValueChange={(search) => {
              if (search) {
                onChange(search);
              }
            }}
          />
          <CommandList className="max-h-[180px] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <CommandEmpty>
              <div className="py-3 text-center">
                <p className="text-sm text-muted-foreground">{emptyText}</p>
                {value && (
                  <p className="text-xs text-primary mt-1 font-medium">
                    Using &quot;{value}&quot;
                  </p>
                )}
              </div>
            </CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => {
                    onChange(opt === value ? '' : opt);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      value === opt ? 'opacity-100 text-primary' : 'opacity-0'
                    )}
                  />
                  <span className="truncate">{opt}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function AddVariantForm({
  modelId,
  existingVariants,
  onSuccess,
}: Readonly<{
  modelId: string;
  existingVariants: DeviceVariantListItem[];
  onSuccess: () => void;
}>) {
  const { t } = useTranslation();
  const { toast } = useToast();

  // Build unique option lists from existing variants + common presets
  const storageOptions = useMemo(() => {
    const fromVariants = existingVariants
      .map((v) => v.storageCapacity)
      .filter((s): s is string => !!s);
    const merged = [...new Set([...COMMON_STORAGE_OPTIONS, ...fromVariants])];
    return merged.sort((a, b) => {
      const numA = parseInt(a) || 0;
      const numB = parseInt(b) || 0;
      return numA - numB;
    });
  }, [existingVariants]);

  const colorOptions = useMemo(() => {
    const fromVariants = existingVariants
      .map((v) => v.color)
      .filter((c): c is string => !!c);
    const merged = [...new Set([...COMMON_COLOR_OPTIONS, ...fromVariants])];
    return merged.sort();
  }, [existingVariants]);

  const ramOptions = useMemo(() => {
    const fromVariants = existingVariants
      .map((v) => v.ram)
      .filter((r): r is string => !!r);
    const merged = [...new Set([...COMMON_RAM_OPTIONS, ...fromVariants])];
    return merged.sort((a, b) => {
      const numA = parseInt(a) || 0;
      const numB = parseInt(b) || 0;
      return numA - numB;
    });
  }, [existingVariants]);

  const form = useForm<CreateDeviceVariant>({
    resolver: zodResolver(CreateDeviceVariantSchema),
    defaultValues: {
      storageCapacity: '',
      color: '',
      ram: '',
      purchasePrice: 0,
      retailPrice: 0,
      isActive: true,
    },
  });

  const mutation = useMutation({
    mutationFn: (data: CreateDeviceVariant) => {
      // Transform empty strings to undefined for optional fields
      // Backend expects null/absent, not empty strings
      const cleaned: CreateDeviceVariant = {
        ...data,
        storageCapacity: data.storageCapacity?.trim() || undefined,
        color: data.color?.trim() || undefined,
        ram: data.ram?.trim() || undefined,
      };
      return createVariant(modelId, cleaned);
    },
    onSuccess: () => {
      // handleApiResponse only returns on success (throws on failure)
      toast({
        title: t('common.success'),
        description: t('deviceModels.toast.variantCreated'),
      });
      onSuccess();
    },
    onError: (error: Error) => {
      // Extract meaningful error message from ApiError
      const message =
        'errors' in error &&
        Array.isArray((error as any).errors) &&
        (error as any).errors.length > 0
          ? (error as any).errors[0]
          : error.message || t('deviceModels.toast.variantCreateFailed');
      toast({
        title: t('common.error'),
        description: message,
        variant: 'destructive',
      });
    },
  });

  const onFormError = () => {
    toast({
      title: t('common.error'),
      description: t('common.validation.fixErrors', {
        defaultValue: 'Please fill in all required fields correctly.',
      }),
      variant: 'destructive',
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(
          (data) => mutation.mutate(data),
          onFormError
        )}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="storageCapacity"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>
                  {t('deviceModels.variants.columns.storage')}
                </FormLabel>
                <FormControl>
                  <VariantComboboxField
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    options={storageOptions}
                    placeholder={t('deviceModels.variants.selectStorage', {
                      defaultValue: 'Select storage...',
                    })}
                    searchPlaceholder={t(
                      'deviceModels.variants.searchStorage',
                      {
                        defaultValue: 'Search or type...',
                      }
                    )}
                    emptyText={t('deviceModels.variants.noStorageFound', {
                      defaultValue: 'No match — type to use custom',
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="color"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>
                  {t('deviceModels.variants.columns.color')}
                </FormLabel>
                <FormControl>
                  <VariantComboboxField
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    options={colorOptions}
                    placeholder={t('deviceModels.variants.selectColor', {
                      defaultValue: 'Select color...',
                    })}
                    searchPlaceholder={t('deviceModels.variants.searchColor', {
                      defaultValue: 'Search or type...',
                    })}
                    emptyText={t('deviceModels.variants.noColorFound', {
                      defaultValue: 'No match — type to use custom',
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="ram"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('deviceModels.variants.columns.ram')}</FormLabel>
                <FormControl>
                  <VariantComboboxField
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    options={ramOptions}
                    placeholder={t('deviceModels.variants.selectRam', {
                      defaultValue: 'Select RAM...',
                    })}
                    searchPlaceholder={t('deviceModels.variants.searchRam', {
                      defaultValue: 'Search or type...',
                    })}
                    emptyText={t('deviceModels.variants.noRamFound', {
                      defaultValue: 'No match — type to use custom',
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="space-y-2">
            <p
              id="variant-sku-label"
              className="text-sm font-medium leading-none"
            >
              {t('deviceModels.variants.columns.sku')}
            </p>
            <p
              role="note"
              aria-labelledby="variant-sku-label"
              aria-describedby="variant-sku-hint"
              className="flex h-10 items-center rounded-md border border-dashed bg-muted/40 px-3 text-sm text-muted-foreground"
            >
              -
            </p>
            <p id="variant-sku-hint" className="text-xs text-muted-foreground">
              {t('deviceModels.variants.skuAutoHint')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="purchasePrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('deviceModels.variants.columns.purchasePrice')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="retailPrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('deviceModels.variants.columns.retailPrice')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <FormLabel>{t('deviceModels.form.isActive')}</FormLabel>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={mutation.isPending} className="w-full">
          {mutation.isPending && <ButtonSpinner className="mr-2" />}
          {t('common.actions.create')}
        </Button>
      </form>
    </Form>
  );
}
